export const MENU_CATEGORY_DEFINITIONS = [
  { name: 'Snacks', icon: 'Sandwich' },
  { name: 'Main Course', icon: 'UtensilsCrossed' },
  { name: 'Beverages', icon: 'CupSoda' },
  { name: 'Dessert', icon: 'IceCream' },
  { name: 'Offers', icon: 'Tag' },
  { name: 'Recommended', icon: 'ThumbsUp' },
  { name: 'Bestseller', icon: 'Award' },
];

/** Fixed homepage category row when dynamic mode is off and no custom list is set. */
export const FIXED_HOMEPAGE_CATEGORIES = MENU_CATEGORY_DEFINITIONS.slice(0, 5).map(
  ({ name }) => name,
);

/** Sanitize a restaurant-specific homepage category list. Returns null when empty/invalid. */
export const normalizeHomepageCategories = (value) => {
  if (!Array.isArray(value)) return null;

  const cleaned = value.map((entry) => String(entry ?? '').trim()).filter(Boolean);
  return cleaned.length > 0 ? cleaned : null;
};

/** Categories shown on homepage when dynamic mode is off. */
export const resolveFixedHomepageCategories = (customCategories, platformDefaults) =>
  normalizeHomepageCategories(customCategories) ||
  normalizeHomepageCategories(platformDefaults) ||
  FIXED_HOMEPAGE_CATEGORIES;

/** Apply a saved rank order onto a category list. Unknown categories append at the end. */
export const applyCategoryOrder = (categoryNames, orderPreference) => {
  const preferred = normalizeHomepageCategories(orderPreference);
  if (!Array.isArray(categoryNames) || categoryNames.length === 0) {
    return preferred || [];
  }
  if (!preferred) return categoryNames;

  const ordered = preferred.filter((name) => categoryNames.includes(name));
  const remaining = categoryNames.filter((name) => !ordered.includes(name));
  const planned = preferred.filter((name) => !categoryNames.includes(name));
  return [...ordered, ...remaining, ...planned];
};

/** Append a category name to the restaurant list (creates custom list from defaults if needed). */
export const appendHomepageCategory = (existingCategories, platformDefaults, newName) => {
  const trimmed = String(newName ?? '').trim();
  if (!trimmed) return null;

  const base = resolveFixedHomepageCategories(existingCategories, platformDefaults);
  if (base.some((name) => name.toLowerCase() === trimmed.toLowerCase())) {
    return base;
  }

  return [...base, trimmed];
};

/** Remove a category name from the restaurant list. Returns null when only platform defaults remain. */
export const removeHomepageCategory = (existingCategories, platformDefaults, nameToRemove) => {
  const trimmed = String(nameToRemove ?? '').trim();
  if (!trimmed) return normalizeHomepageCategories(existingCategories);

  const base = resolveFixedHomepageCategories(existingCategories, platformDefaults);
  const next = base.filter((name) => name.toLowerCase() !== trimmed.toLowerCase());
  const normalized = normalizeHomepageCategories(next);
  const platformOnly = normalizeHomepageCategories(platformDefaults);

  if (
    !normalized ||
    (platformOnly &&
      normalized.length === platformOnly.length &&
      normalized.every((name, index) => name.toLowerCase() === platformOnly[index]?.toLowerCase()))
  ) {
    return null;
  }

  return normalized;
};

/** Rebuild saved homepage_categories with custom names reordered (defaults stay first). */
export const buildHomepageCategoriesWithCustomOrder = (
  customOrder,
  existingCategories,
  platformDefaults,
  allCategoryNames = [],
) => {
  const defaultsLower = new Set(platformDefaults.map((name) => name.toLowerCase()));
  const configured = resolveFixedHomepageCategories(existingCategories, platformDefaults);
  const merged = [...configured];

  for (const name of allCategoryNames) {
    if (!merged.some((entry) => entry.toLowerCase() === name.toLowerCase())) {
      merged.push(name);
    }
  }

  const defaults = merged.filter((name) => defaultsLower.has(name.toLowerCase()));
  const customOrdered = (Array.isArray(customOrder) ? customOrder : [])
    .map((entry) => String(entry ?? '').trim())
    .filter(Boolean);
  const customSet = new Set(customOrdered.map((name) => name.toLowerCase()));
  const remaining = merged.filter(
    (name) => !defaultsLower.has(name.toLowerCase()) && !customSet.has(name.toLowerCase()),
  );

  return normalizeHomepageCategories([...defaults, ...customOrdered, ...remaining]);
};

/** Unique non-empty categories from menu items, in first-seen order. */
export const deriveCategoriesFromItems = (items = []) => {
  const seen = new Set();
  const result = [];

  for (const item of items) {
    const category = item?.category?.trim();
    if (!category || seen.has(category)) continue;
    seen.add(category);
    result.push(category);
  }

  return result;
};

/** Homepage category buttons: custom/fixed list or derived from menu items. */
export const resolveHomepageCategories = (
  allFoodItems,
  dynamicEnabled,
  customCategories,
  platformDefaults,
) => {
  const fixedFallback = resolveFixedHomepageCategories(customCategories, platformDefaults);

  if (dynamicEnabled) {
    const derived = deriveCategoriesFromItems(allFoodItems);
    const base = derived.length > 0 ? derived : fixedFallback;
    return applyCategoryOrder(base, customCategories);
  }

  return fixedFallback;
};

/** Category options for menu item forms and menu management filters. */
export const resolveMenuCategoryOptions = (restaurant, allFoodItems = [], platformDefaults) => {
  const savedOrder = restaurant?.homepage_categories;
  const configured = resolveFixedHomepageCategories(savedOrder, platformDefaults);
  const fromItems = deriveCategoriesFromItems(allFoodItems);

  const mergeUnique = (base, extras) => {
    const merged = [...base];
    for (const name of extras) {
      if (!merged.some((entry) => entry.toLowerCase() === name.toLowerCase())) {
        merged.push(name);
      }
    }
    return merged;
  };

  let names;
  if (restaurant?.dynamic_categories_enabled === true) {
    const base = fromItems.length > 0 ? fromItems : configured;
    names = applyCategoryOrder(base, savedOrder);
  } else {
    names = applyCategoryOrder(mergeUnique(configured, fromItems), savedOrder);
  }

  return names.map((name) => {
    const known = MENU_CATEGORY_DEFINITIONS.find((entry) => entry.name === name);
    return known || { name, icon: 'LayoutGrid' };
  });
};
